import React, {useEffect, useMemo, useState} from "react";
import { useTranslation } from "react-i18next";
import classNames from 'classnames';
import { Grid, MenuItem, TextField } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import _ from "lodash";
import {apiClient} from "../../../providers/apiClient";
import {APP_URLS} from "../../../app/config";

function CompanyInfoForm({ isCreate, canEdit, onUpdate, model, errors, sectors, onFieldChange }) {
    const { t } = useTranslation('CompanyCreateScreen', { keyPrefix: 'InfoForm' });

    const [currentEdit, setCurrentEdit] = React.useState('');



    const checkIfDisabled = (field) => !isCreate && (!canEdit || field !== currentEdit);
    const getErrorMessage = (field) => _.find(errors, (error) => error.field === field);
    
    const handleUpdate = async (name, value) => {
        if (onUpdate) {
            await onUpdate(name, value);
        }
        setCurrentEdit(null);
    }

    useEffect(() => {
        document.documentElement.style.setProperty('--blocked-sector-position-text', `'${t('blockedSectorPositionText')}'`);
    }, [t])

    const companySectors = useMemo(() => {
        return sectors
            .filter(sector => model?.allSectors.includes(sector.value))
            .map(sector => sector.text)
            .join(', ');
    }, [model]);

    if (!model) {
        return null;
    }

    // Prepare dynamic options for allSectors
    const allSectorsOptions = sectors.map(sector => ({
        value: sector.id,
        text: sector.name
    }));

    // Placeholder for status options, replace with actual source if available
    const statusOptions = [
        { value: 'active', text: t('statusActive') },
        { value: 'inactive', text: t('statusInactive') },
    ];

    return (
        <div
            className={
                classNames(
                    'fk-form-section',
                    {
                        'edit-mode': canEdit
                    }
                )
            }
        >
            <h3 className="title">
                {t('title')}
            </h3>
            <Grid container spacing={2}>
                {/* Field: name */}
                <React.Fragment>
                    <Grid item md={3} className="fk-label">
                        <label htmlFor="name">{`${t('name')}${true ? '*' : ''}`}</label>
                    </Grid>
                    <Grid item md={8} className={
                        classNames(
                            'fk-input-container',
                            {
                                'edit-mode': canEdit,
                                'has-error': !!getErrorMessage('name'),
                            }
                        )
                    }>
                        <TextField
                            fullWidth
                            disabled={checkIfDisabled('name')}
                            className="fk-input"
                            placeholder={t('namePlaceholder')}
                            helperText={getErrorMessage('name')?.error}
                            value={model?.name ?? ''}
                            onChange={(event) => onFieldChange && onFieldChange('name', event.target.value)}
                        />
                    </Grid>
                    {canEdit ?
                        <Grid item md={1}>
                            {'name' !== currentEdit
                                ? <button className="fk-edit-button" onClick={() => setCurrentEdit('name')}><EditIcon /></button>
                                : <button className="fk-edit-button" onClick={() => handleUpdate('Name', model.name)}><SaveIcon /></button>
                            }
                        </Grid>
                        : <Grid item md={1} />
                    }
                </React.Fragment>

                {/* Field: nip */}
                <React.Fragment>
                    <Grid item md={3} className="fk-label">
                        <label htmlFor="nip">{`${t('nip')}`}</label>
                    </Grid>
                    <Grid item md={8} className={
                        classNames(
                            'fk-input-container',
                            {
                                'edit-mode': canEdit,
                                'has-error': !!getErrorMessage('nip'),
                            }
                        )
                    }>
                        <TextField
                            fullWidth
                            disabled={checkIfDisabled('nip')}
                            className="fk-input"
                            placeholder={t('nipPlaceholder')}
                            helperText={getErrorMessage('nip')?.error}
                            value={model?.nip ?? ''}
                            onChange={(event) => onFieldChange && onFieldChange('nip', event.target.value)}
                        />
                    </Grid>
                    {canEdit ?
                        <Grid item md={1}>
                            {'nip' !== currentEdit
                                ? <button className="fk-edit-button" onClick={() => setCurrentEdit('nip')}><EditIcon /></button>
                                : <button className="fk-edit-button" onClick={() => handleUpdate('NIP', model.nip)}><SaveIcon /></button>
                            }
                        </Grid>
                        : <Grid item md={1} />
                    }
                </React.Fragment>

                {/* Field: allSectors */}
                <React.Fragment>
                    <Grid item md={3} className="fk-label">
                        <label htmlFor="allSectors">{`${t('allSectors')}${true ? '*' : ''}`}</label>
                    </Grid>
                    <Grid item md={8} className={
                        classNames(
                            'fk-input-container',
                            {
                                'edit-mode': canEdit,
                                'has-error': !!getErrorMessage('allSectors'),
                            }
                        )
                    }>
                        <TextField
                            fullWidth
                            disabled={checkIfDisabled('allSectors')}
                            className="fk-input"
                            placeholder={t('allSectorsPlaceholder')}
                            helperText={getErrorMessage('allSectors')?.error}
                            value={companySectors}
                            onChange={(event) => onFieldChange && onFieldChange('allSectors', event.target.value)}
                        />
                    </Grid>
                </React.Fragment>

                {/* Field: email */}
                <React.Fragment>
                    <Grid item md={3} className="fk-label">
                        <label htmlFor="email">{`${t('email')}${true ? '*' : ''}`}</label>
                    </Grid>
                    <Grid item md={8} className={
                        classNames(
                            'fk-input-container',
                            {
                                'edit-mode': canEdit,
                                'has-error': !!getErrorMessage('email'),
                            }
                        )
                    }>
                        <TextField
                            fullWidth
                            disabled={checkIfDisabled('email')}
                            className="fk-input"
                            placeholder={t('emailPlaceholder')}
                            helperText={getErrorMessage('email')?.error}
                            value={model?.email ?? ''}
                            onChange={(event) => onFieldChange && onFieldChange('email', event.target.value)}
                        />
                    </Grid>
                    {canEdit ?
                        <Grid item md={1}>
                            {'email' !== currentEdit
                                ? <button className="fk-edit-button" onClick={() => setCurrentEdit('email')}><EditIcon /></button>
                                : <button className="fk-edit-button" onClick={() => handleUpdate('Email', model.email)}><SaveIcon /></button>
                            }
                        </Grid>
                        : <Grid item md={1} />
                    }
                </React.Fragment>

                {/* Field: status */}
                <React.Fragment>
                    <Grid item md={3} className="fk-label">
                        <label htmlFor="status">{`${t('status')}`}</label>
                    </Grid>
                    <Grid item md={8} className={
                        classNames(
                            'fk-input-container',
                            {
                                'edit-mode': canEdit,
                                'has-error': !!getErrorMessage('status'),
                            }
                        )
                    }>
                        <TextField
                            fullWidth
                            select
                            disabled={checkIfDisabled('status')}
                            className="fk-input"
                            placeholder={t('statusPlaceholder')}
                            helperText={getErrorMessage('status')?.error}
                            value={model?.status ?? ''}
                            onChange={(event) => onFieldChange && onFieldChange('status', event.target.value)}
                        >
                            {statusOptions.map((item, index) => (
                                <MenuItem key={index} value={item.value}>
                                    {item.text}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    {canEdit ?
                        <Grid item md={1}>
                            {'status' !== currentEdit
                                ? <button className="fk-edit-button" onClick={() => setCurrentEdit('status')}><EditIcon /></button>
                                : <button className="fk-edit-button" onClick={() => handleUpdate('Status', model.status)}><SaveIcon /></button>
                            }
                        </Grid>
                        : <Grid item md={1} />
                    }
                </React.Fragment>

                {/* Field: ownName */}
                <React.Fragment>
                    <Grid item md={3} className="fk-label">
                        <label htmlFor="ownName">{`${t('ownName')}`}</label>
                    </Grid>
                    <Grid item md={8} className={
                        classNames(
                            'fk-input-container',
                            {
                                'edit-mode': canEdit,
                                'has-error': !!getErrorMessage('ownName'),
                            }
                        )
                    }>
                        <TextField
                            fullWidth
                            disabled={checkIfDisabled('ownName')}
                            className="fk-input"
                            placeholder={t('ownNamePlaceholder')}
                            helperText={getErrorMessage('ownName')?.error}
                            value={model?.ownName ?? ''}
                            onChange={(event) => onFieldChange && onFieldChange('ownName', event.target.value)}
                        />
                    </Grid>
                    {canEdit ?
                        <Grid item md={1}>
                            {'ownName' !== currentEdit
                                ? <button className="fk-edit-button" onClick={() => setCurrentEdit('ownName')}><EditIcon /></button>
                                : <button className="fk-edit-button" onClick={() => handleUpdate('OwnName', model.ownName)}><SaveIcon /></button>
                            }
                        </Grid>
                        : <Grid item md={1} />
                    }
                </React.Fragment>
            </Grid>
        </div>
    );
}

export default CompanyInfoForm;